import type { Appointment } from '../../types/appointments.js';

// How an appointment is DESCRIBED on a screen (the staff conversation detail). Pure, so every sentence is tested
// without jsdom. Anytime Fitness bookings carry a provider badge, the staff member's initials (AF names never
// reach our records) and the slot-gone EVIDENCE line.

export type AppointmentProviderKind = 'anytime_fitness' | 'google' | 'internal';

export function appointmentProviderKind(a: Pick<Appointment, 'provider'>): AppointmentProviderKind {
  return a.provider === 'anytime_fitness' || a.provider === 'google' ? a.provider : 'internal';
}

export function appointmentProviderLabel(a: Pick<Appointment, 'provider'>): string {
  switch (appointmentProviderKind(a)) {
    case 'anytime_fitness':
      return 'Anytime Fitness calendar';
    case 'google':
      return 'Google calendar';
    default:
      return 'Kyck calendar';
  }
}

/** "with J.S." for an AF booking; the stored member name for anything else. */
export function appointmentStaffLabel(a: Pick<Appointment, 'provider' | 'afStaffInitials' | 'memberDisplayName'>): string {
  if (appointmentProviderKind(a) === 'anytime_fitness') {
    const initials = a.afStaffInitials?.trim();
    return initials ? `with ${initials}` : 'with club staff';
  }
  return a.memberDisplayName ? `with ${a.memberDisplayName}` : '';
}

/**
 * The evidence line, AF bookings only. ⚠️ Evidence, not a verdict: AF returns no booking id, so "the time dropped
 * out of AF's availability" is the only after-the-fact sign the booking took. It never changes what the lead was told.
 */
export function slotGoneEvidenceLine(a: Pick<Appointment, 'provider' | 'slotGoneConfirmed'>): string | null {
  if (appointmentProviderKind(a) !== 'anytime_fitness') return null;
  if (a.slotGoneConfirmed === true) return 'Confirmed: the time dropped out of Anytime Fitness availability after booking.';
  if (a.slotGoneConfirmed === false) {
    return 'Check ClubOS: the time was still listed as available in Anytime Fitness a minute after booking. It may just be slow to update.';
  }
  return 'Not checked yet: Anytime Fitness gives no booking id, so we look at its availability about 20 seconds after booking.';
}
